// @flow

import * as React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import i18n from 'app/common/i18n';
import DataTable from 'app/common/table/Table';

import CellContextMenu from './CellContextMenu';
import CellStatus from './CellStatus';
import CellRelativeTime from './CellRelativeTime';
import CellStatusFilter from './CellStatusFilter';

import { getDwellingStatus } from '../StatusChip';

import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle
} from '@mui/material';

import { styled } from '@mui/material/styles';
import green from '@mui/material/colors/green';
import red from '@mui/material/colors/red';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';

import Button from 'app/common/button';
import AutoCompleteDAWA from 'app/common/autocomplete-dawa';

import ActivityDialog from '../dialog-activities';

import {
  duplicate as duplicateDwelling,
  toggleActivation,
  reject
} from 'app/redux/modules/dwellings';
import { connect } from 'react-redux';

import { Query } from '../../common/Query';
import { Mutation } from '../../common/Mutation';
import qs from 'qs';

const Root = styled('div')({
  '& .dwellings-row-dimmed': {
    opacity: 0.5
  },

  '& .dwellings-row-approved': {
    backgroundColor: green[50]
  },

  '& .dwellings-row-rejected': {
    backgroundColor: red[50]
  }
});

const classes = {
  dimmed: 'dwellings-row-dimmed',
  approved: 'dwellings-row-approved',
  rejected: 'dwellings-row-rejected'
};

type State = {
  audit: string | null,
  duplicate: { uuid: string, location: mixed } | null,
  columnData: Array<{
    id: string,
    label: string,
    locked?: boolean,
    padding?: 'none' | 'default' | 'checkbox',
    cellComponent?: mixed,
    getValue?: (mixed) => mixed
  }>,
  rowsPerPage: number,
  after: string | null,
  before: string | null
};

const DWELLINGS = `
query DwellingConnection($query: DwellingQueryInput, $filter: DwellingFilterInput!, $sortBy: DwellingSortByOption, $sortDir: ConnectionSortDir, $before: String, $after: String, $first: Int, $last: Int) {
  dwellingConnection(query: $query, filter: $filter, sortBy: $sortBy, sortDir: $sortDir, before: $before, after: $after, first: $first, last: $last) {
    totalCount
    pageInfo {
      startCursor
      endCursor
      hasNextPage
      hasPreviousPage
    }
    edges {
      node {
        id
        active
        listingType
        status
        user {
          id
        }
        location {
          address
          street
          zip
          city
        }
        size {
          rooms
          area
        }
        economy {
          rent
        }
        contact {
          contactName: name
          contactEmail: email
          contactPhone: phone
        }
        createdAt
        updatedAt
        approvedAt
        expiresAt
        fromFacebook
        customAddress
        deactivatedAt
        deactivatedBy {
          name
          initials
        }
      }
    }
  }
}
`;

export const EXTEND_DWELLING = `
mutation extendDwelling($dwellingId: ID!) {
  extendDwelling(id: $dwellingId) {
    id
  }
}
`;

function serializeQuery(query) {
  const q = { ...query };
  if (q.status === 'DEACTIVATED') {
    delete q.status;
    q.active = false;
  } else if (![null, '', 'REJECTED'].includes(query.status)) {
    q.active = true;
  }
  return q;
}

function serialize(values) {
  const { query, filter, ...rest } = values;
  delete filter.contextMenu;
  return {
    query: serializeQuery(query),
    filter: { ...filter, contextMenu: undefined },
    ...rest
  };
}

function dataSerializer(data) {
  return data.map(({ node }) => {
    const { contact = {}, economy = {}, size = {}, ...rest } = node;
    return {
      node: {
        ...contact,
        ...economy,
        ...size,
        ...rest
      }
    };
  });
}

const notAllowedQueryKeys = ['email', 'name', 'phone'];
const getQueryFromUrl = (hash) => {
  if (!hash) {
    return {};
  }
  const req = /^#/;
  const parsedUrl = qs.parse(hash.replace(req, ''));
  return Object.keys(parsedUrl).reduce((acc, key) => {
    if (notAllowedQueryKeys.includes(key)) {
      return acc;
    }
    if (key === 'fromFacebook') {
      acc[key] =
        parsedUrl[key] === 'false'
          ? false
          : parsedUrl[key] === 'true'
          ? true
          : null;
    } else {
      acc[key] = parsedUrl[key];
    }
    return acc;
  }, {});
};

const sanitizeAddress = (location) => {
  const hasZip = new RegExp(location.zip).test(location.address);
  const hasCity = new RegExp(location.city).test(location.address);
  if (hasZip && hasCity) {
    return `${(location.address || '').replace(
      /^(.*), ([0-9]{4} .*)$/,
      '$1\n$2'
    )}`;
  }

  if (hasZip) {
    return `${(location.address || '').replace(
      /^(.*), ([0-9]{4} .*)$/,
      '$1\n$2'
    )} ${location.city}`;
  }

  if (hasCity) {
    return `${(location.address || '').replace(
      /^(.*), ([0-9]{4} .*)$/,
      '$1\n$2'
    )} ${location.zip}`;
  }

  if (location.address && location.zip && location.city) {
    return `${(location.address || '').replace(
      /^(.*), ([0-9]{4} .*)$/,
      '$1\n$2'
    )}, ${location.zip} ${location.city}`;
  }

  if (location.address && (location.zip || location.city)) {
    return `${(location.address || '').replace(
      /^(.*), ([0-9]{4} .*)$/,
      '$1\n$2'
    )}, ${location.zip || location.city}`;
  }

  if (location.address) {
    return `${(location.address || '').replace(
      /^(.*), ([0-9]{4} .*)$/,
      '$1\n$2'
    )}`;
  }

  if (location.street && location.zip && location.city) {
    return `${location.street}, ${location.zip} ${location.city}`;
  }

  if (location.street && location.zip) {
    return `${location.street}, ${location.zip}`;
  }

  if (location.street && location.city) {
    return `${location.street}, ${location.city}`;
  }

  if (location.zip && location.city) {
    return `${location.zip} ${location.city}`;
  }

  if (location.street) {
    return `${location.street}, ${location.city}`;
  }

  if (location.city) {
    return `${location.city}`;
  }

  if (location.zip) {
    return `${location.zip}`;
  }

  return '';
};

class DwellingsTable extends React.Component<Props, State> {
  i18n: (string) => string;

  constructor(props: Props) {
    super(props);
    const urlQuery = getQueryFromUrl(props.location.hash);

    this.state = {
      audit: null,
      duplicate: null,
      columnData: [
        {
          locked: true,
          id: 'contextMenu',
          label: '',
          padding: 'checkbox',
          cellComponent: CellContextMenu
        },
        {
          id: 'status',
          label: props.i18n('Status'),
          filter: CellStatusFilter,
          cellComponent: CellStatus,
          getValue: (dwelling) => getDwellingStatus(dwelling)
        },
        {
          id: 'address',
          label: props.i18n('Address'),
          padding: 'none',
          filter: true,
          getValue: (row) => sanitizeAddress(row.location)
        },
        {
          id: 'rent',
          align: 'right',
          label: props.i18n('Rent')
        },
        {
          id: 'rooms',
          align: 'right',
          label: props.i18n('Rooms')
        },
        {
          id: 'area',
          align: 'right',
          label: props.i18n('Area')
        },
        {
          id: 'contactName',
          label: props.i18n('Contact name'),
          filter: true
        },
        {
          id: 'contactEmail',
          label: props.i18n('Contact email'),
          filter: true
        },
        {
          id: 'contactPhone',
          label: props.i18n('Contact phone'),
          filter: true
        },
        {
          id: 'fromFacebook',
          label: props.i18n('Facebook'),
          filter: true,
          checkbox: true,
          getValue: (row) => (row.fromFacebook ? 'FB' : '-')
        },
        {
          id: 'listingType',
          label: props.i18n('Listing type'),
          getValue: (row) => (row.listingType ? row.listingType : '-'),
          styles: (row) =>
            row.listingType !== 'STANDARD'
              ? { fontWeight: 'bold' }
              : { color: '#aaa' }
        },
        {
          id: 'createdAt',
          align: 'right',
          label: props.i18n('Created at'),
          cellComponent: CellRelativeTime
        },
        {
          id: 'updatedAt',
          align: 'right',
          label: props.i18n('Updated at'),
          cellComponent: CellRelativeTime
        },
        {
          id: 'approvedAt',
          align: 'right',
          label: props.i18n('Approved at'),
          cellComponent: CellRelativeTime
        },
        {
          id: 'expiresAt',
          align: 'right',
          label: props.i18n('Expires at'),
          cellComponent: CellRelativeTime
        },
        {
          id: 'deactivatedAt',
          align: 'right',
          label: props.i18n('Deactivated at'),
          cellComponent: CellRelativeTime
        },
        {
          id: 'deactivatedBy',
          label: props.i18n('Deactivated by')
        }
      ],
      query: {
        status: 'PENDING_REVIEW',
        ...(this.props.userId
          ? { user: this.props.userId, status: 'APPROVED' }
          : {}),
        ...urlQuery
      },
      filter: {
        status: true,
        address: true,
        rent: false,
        rooms: false,
        area: false,
        contactName: true,
        contactEmail: true,
        contactPhone: true,
        fromFacebook: false,
        listingType: true,
        createdAt: true,
        updatedAt: true,
        approvedAt: false,
        expiresAt: false,
        deactivatedAt: false,
        deactivatedBy: false
      },
      sortBy: props.orderBy || 'CREATED_AT',
      sortDir: 'desc',
      rowsPerPage: this.props.rowsPerPage || 25,
      after: null,
      before: null
    };
    this.setVariables = this.setVariables.bind(this);
    this.onPaginationHandler = this.onPaginationHandler.bind(this);
  }

  handleDuplicate = (duplicate) => this.setState({ duplicate });

  confirmDuplicate = () => {
    const { id, location } = this.state.duplicate;
    this.props.duplicateDwelling(id, location).then((result) => {
      this.props.navigate(`/dwellings/${result.payload.uuid}`);
    });
  };

  handleAudit = (uuid) => this.setState({ audit: uuid });
  handleAuditClose = () => this.setState({ audit: null });

  setVariables(name: string, variables: Object | string | number) {
    const partial = {};
    if (name === 'query' || name === 'rowsPerPage') {
      partial.after = null;
      partial.before = null;
    }
    if (typeof variables === 'object') {
      this.setState((state) => ({
        [name]: { ...state[name], ...variables },
        ...partial
      }));
    } else {
      this.setState({ [name]: variables, ...partial });
    }
  }

  onPaginationHandler(page, pageInfo, increment) {
    if (increment === 1) {
      this.setState({ after: pageInfo.endCursor, before: null });
    } else if (increment === -1) {
      this.setState({ before: pageInfo.startCursor, after: null });
    }
  }

  render() {
    const { ...props } = this.props;
    const {
      query,
      filter,
      sortBy,
      sortDir,
      before,
      after,
      rowsPerPage
    } = this.state;
    const variables = serialize({
      query,
      filter,
      sortBy,
      sortDir: sortDir.toUpperCase(),
      before,
      after,
      first: (!before && rowsPerPage) || null,
      last: before && rowsPerPage
    });

    return (
      <Root>
        <Query query={DWELLINGS} variables={variables} loading={false}>
          {({ data: res, loading, refetch }) => {
            const data = res && res.dwellingConnection;
            const facebookOnlyData = (props.data || data?.edges || []).filter(
              (v) => v.node.fromFacebook
            );
            return (
              <Mutation mutation={EXTEND_DWELLING}>
                {(mutate) => {
                  return (
                    <DataTable
                      saveCookieAs="dwellings-columns"
                      loading={loading}
                      refetch={refetch}
                      rowProps={{
                        refetch,
                        onDuplicate: this.handleDuplicate,
                        onAuditOpen: this.handleAudit,
                        onExtendDwelling: mutate,
                        toggleActivation: this.props.toggleActivation,
                        reject: this.props.reject,
                        classes: (row) => ({
                          root: [
                            !row.active && classes.dimmed,
                            row.status === 'APPROVED' &&
                              row.active &&
                              classes.approved,
                            row.status === 'REJECTED' && classes.rejected
                          ]
                            .filter(Boolean)
                            .join(' ')
                        })
                      }}
                      primaryKey="id"
                      order={sortDir}
                      orderBy={sortBy}
                      columnData={this.state.columnData}
                      setVariables={this.setVariables}
                      defaultHiddenColumnNames={Object.keys(filter)
                        .map((key) => filter[key] === false && key)
                        .filter(Boolean)}
                      defaultFilters={this.state.query}
                      defaultRowsPerPage={this.state.rowsPerPage}
                      onPaginationHandler={this.onPaginationHandler}
                      totalCount={(data && data.totalCount) || 0}
                      pageInfo={(data && data.pageInfo) || {}}
                      {...props}
                      facebookOnlyData={dataSerializer(facebookOnlyData)}
                      data={dataSerializer(
                        props.data || (data && data.edges) || []
                      )}
                    />
                  );
                }}
              </Mutation>
            );
          }}
        </Query>

        {this.state.duplicate ? (
          <Dialog
            open
            fullScreen={this.props.fullScreen}
            onClose={() => this.setState({ duplicate: null })}
          >
            <DialogTitle>{`${this.props.i18n('Duplicate')} ${
              this.state.duplicate.address
            }`}</DialogTitle>
            <DialogContent
              style={{
                width: 480,
                maxWidth: '80vw',
                height: 600,
                maxHeight: '80vh'
              }}
            >
              <AutoCompleteDAWA
                label={this.props.i18n('Address')}
                onSelect={(choosen, location) =>
                  this.setState({
                    duplicate: { ...this.state.duplicate, location }
                  })
                }
                placeholder="fx Marielundvej 20, 2730 Herlev"
              />
            </DialogContent>
            <DialogActions>
              <Button
                flat
                primary
                label={this.props.i18n('Cancel')}
                onClick={() => this.setState({ duplicate: null })}
              />
              <Button
                flat
                primary
                label={this.props.i18n('Duplicate')}
                onClick={this.confirmDuplicate}
              />
            </DialogActions>
          </Dialog>
        ) : this.state.audit ? (
          <ActivityDialog
            open
            uuid={this.state.audit}
            onClose={this.handleAuditClose}
          />
        ) : null}
      </Root>
    );
  }
}

function withMobileDialogCompat(Component) {
  return function WithMobileDialogCompat(props) {
    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));

    return <Component {...props} fullScreen={fullScreen} />;
  };
}

function DwellingsTableWithRouter(props) {
  const navigate = useNavigate();
  const location = useLocation();

  return <DwellingsTable {...props} navigate={navigate} location={location} />;
}

export default connect(() => ({}), {
  duplicateDwelling,
  toggleActivation,
  reject
})(withMobileDialogCompat(i18n(DwellingsTableWithRouter)));
